import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { jsPDF } from "jspdf";
import autoTableImport from "jspdf-autotable";
import axios from "axios";
import PropTypes from "prop-types";
import logo from "./principal.jpg";
const autoTable =
  autoTableImport && autoTableImport.default ? autoTableImport.default : autoTableImport;

export default function ExamTimeTablePDF({ clientId, examScheduleMasterId, onDownloaded }) {
  const [timetables, setTimetables] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [collegeLogo, setCollegeLogo] = useState(null);
  const [downloaded, setDownloaded] = useState(false); // ✅ Track if already downloaded

  // Reset downloaded flag whenever master id changes (defensive)
  useEffect(() => {
    setDownloaded(false);
  }, [examScheduleMasterId]);

  useEffect(() => {
    if (!clientId || !examScheduleMasterId) return;
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/exam-timetables/timetables/${clientId}/${examScheduleMasterId}`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
          params: { t: new Date().getTime() }, // prevent caching
        }
      )
      .then((res) => {
        if (res.data?.success) {
          const data = res.data.data || [];
          setTimetables(data);

          if (data.length > 0 && data[0].college) {
            setCollegeInfo(data[0].college);

            // Load logo if path available
            if (data[0].college.college_logopath) {
              const img = new Image();
              //img.crossOrigin = "Anonymous"; // needed for CORS images
              img.src = `${process.env.REACT_APP_API_BASE_URL}/uploads/logos/${clientId}/${data[0].college.society_id}/${data[0].college.college_id}/${data[0].college.college_logopath}`;
              img.onload = () => {
                setCollegeLogo(img);
              };
            }
          }
        }
      })
      .catch((err) => console.error("Failed to fetch timetables:", err));
  }, [clientId, examScheduleMasterId]);

  const groupKeyFor = (row) =>
    `${row.program_id}_${row.branch_id}_${row.exam_schedule_detail?.year_number}_${row.exam_schedule_detail?.semester_number}`;

  const handleDownloadPDF = () => {
    if (!Array.isArray(timetables) || timetables.length === 0) {
      alert("No timetable data to export");
      return;
    }

    if (typeof autoTable !== "function") {
      alert("PDF export unavailable: autoTable plugin not loaded.");
      return;
    }

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const toRoman = (num) => {
      const romans = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
      return romans[num] || num || "-";
    };
    // --- Header: College info (once, on first page) ---
    const renderCollegeHeader = () => {
      if (!collegeInfo) return;

      const margin = 15;
      const topY = 15;
      const logoWidth = 30;
      const logoHeight = 30;
      const pageWidth = doc.internal.pageSize.getWidth();

      let textX = margin;
      let y = topY + 8; // baseline for text

      // --- 1️⃣ If logo is available ---
      if (collegeLogo) {
        doc.addImage(collegeLogo, "PNG", margin, topY, logoWidth, logoHeight);
        textX = margin + logoWidth + 10; // shift text right after logo
      }

      // --- 2️⃣ Calculate alignment ---
      // If no logo, center the college name
      const align = collegeLogo ? "left" : "center";
      const centerX = pageWidth / 2;

      // --- 3️⃣ College Name ---
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(collegeInfo.college_name || "", collegeLogo ? textX : centerX, y, { align });

      // --- 4️⃣ Details line (Affiliation, Accreditation, etc.) ---
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      let details = [];

      if (collegeInfo.is_autonomous !== undefined) {
        const autoText = collegeInfo.is_autonomous ? "AUTONOMOUS" : "AFFILIATED";
        details.push(autoText);
      }
      if (collegeInfo.accreditation) details.push(collegeInfo.accreditation);
      if (collegeInfo.affiliation) details.push(collegeInfo.affiliation);

      if (details.length > 0) {
        y += 6;
        doc.text(details.join(" , "), collegeLogo ? textX : centerX, y, { align });
      }

      // --- 5️⃣ Address line ---
      const address = [
        collegeInfo.college_location,
        collegeInfo.college_district,
        collegeInfo.college_state,
        collegeInfo.college_pincode,
      ]
        .filter(Boolean)
        .join(", ");

      if (address) {
        y += 6;
        doc.text(address, collegeLogo ? textX : centerX, y, { align });
      }
    };

    // group rows
    const groups = {};
    timetables.forEach((r) => {
      const key = groupKeyFor(r);
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    let pdfTitle = "";
    const groupKeys = Object.keys(groups);
    groupKeys.forEach((key, pageIndex) => {
      const rows = groups[key];

      if (pageIndex > 0) doc.addPage();

      renderCollegeHeader();

      // Adjust top margin dynamically based on logo and college info
      const startY = collegeInfo
        ? collegeLogo
          ? 60 // logo + college info
          : 40 // only college info
        : 30; // no college info or logo

      // --- TITLE SECTION ---
      doc.setFontSize(15);
      doc.text("Examination Time Table", pageWidth / 2, startY, { align: "center" });

      // Get details for program, branch, year, semester
      const sample = rows[0];
      const programName = sample.program?.program_name ?? sample.program_name ?? "";
      const branchName = sample.branch?.branch_name ?? sample.branch_name ?? "";
      const yearNumber = toRoman(sample.exam_schedule_detail?.year_number) ?? "";
      const semesterNumber = toRoman(sample.exam_schedule_detail?.semester_number) ?? "";

      // Build title line and filename
      const groupTitle = `${programName} - ${branchName} - ${yearNumber} Year, ${semesterNumber} Semester`;
      const pdfTitle = `${programName}_${yearNumber}_Year_${semesterNumber}_Semester`;

      // Draw group title BELOW the main title (so it never overlaps)
      doc.setFontSize(13);
      doc.text(groupTitle, pageWidth / 2, startY + 8, { align: "center" });
      //doc.text(pdfTitle, pageWidth / 2, startY + 16, { align: "center" });
      const sortedRows = [...rows].sort((a, b) => {
        const dateA = a.from_exam_date ? new Date(a.from_exam_date) : null;
        const dateB = b.from_exam_date ? new Date(b.from_exam_date) : null;

        if (dateA === null && dateB === null) return 0;
        if (dateA === null) return 1;
        if (dateB === null) return -1;

        return dateA - dateB;
      });

      const tableBody = sortedRows.map((r, idx) => [
        idx + 1,
        r.course_code || r.exam_course?.course_code || "",
        r.exam_course?.course_name || r.course_name || "",
        r.from_exam_date ? new Date(r.from_exam_date).toLocaleDateString("en-GB") : "",
        r.exam_slot_desc || r.description || "",
      ]);

      autoTable(doc, {
        head: [["S.No", "Course Code", "Course Name", "Exam Date", "Exam Time"]],
        body: tableBody,
        startY: startY + 15,
        styles: {
          fontSize: 10,
          cellPadding: 3,
          valign: "middle",
        },
        headStyles: {
          fillColor: [22, 78, 135],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center", fontSize: 9 }, // S.No
          1: { cellWidth: 30 }, // Course Code
          2: { cellWidth: 70 }, // Course Name
          3: { cellWidth: 30, halign: "center" }, // Exam Date
          4: { cellWidth: 50, halign: "center", fontSize: 9 }, // Exam Time
        },
        margin: { left: 10, right: 10 },
        theme: "grid",
      });

      // ✅ Add signature after table
      const tableEndY = doc.lastAutoTable?.finalY + 20 ?? startY + 20; // fallback
      doc.setFontSize(11);

      const logoW = 50; // small width
      const logoH = 10; // proportionate height

      const logoX = pageWidth - logoW - 20; // 20mm right margin
      const logoY = tableEndY + 10;

      // 2. Add logo
      doc.addImage(logo, "JPG", logoX, logoY, logoW, logoH);

      // 3. Add text BELOW the image
      doc.text(
        "Signature of the Principal",
        logoX + logoW, // right alignment anchor
        logoY + logoH + 8, // 8px below logo
        { align: "right" }
      );
    });

    doc.save(`ExamTimeTable_${pdfTitle}.pdf`);

    // Hide button after download (internal)
    setDownloaded(true);

    // Inform parent so it can unmount and allow fresh mount on next selection
    try {
      if (typeof onDownloaded === "function") onDownloaded();
    } catch (e) {
      // swallow to avoid breaking UI
      // parent clearing is best-effort; internal state already hides button
    }
  };

  // Do not render button once downloaded (internal)
  if (downloaded) return null;

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handleDownloadPDF}
      sx={{
        backgroundColor: "#1976d2",
        color: "#fff",
        textTransform: "none",
        "&:hover": { backgroundColor: "#115293" },
      }}
    >
      Download TimeTable PDF
    </Button>
  );
}

ExamTimeTablePDF.propTypes = {
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onDownloaded: PropTypes.func,
};
